import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  orderBy,
  query,
} from 'firebase/firestore';
import { collectionData, docData } from 'rxfire/firestore';
import { lastValueFrom, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import dataKeys from '../../constants/cop_data_keys';
import { ICOP, ICOPShardStringified, ICOPStringified, IUser } from '../types';
import { authenticatedUserProvider } from './userProvider';

const copTest = (testId: string, user: IUser): Observable<ICOP> => {
  return docData(
    doc(
      user.services.firestore,
      `orgs/${user.orgId}/cop/${testId}`
    ) as DocumentReference<ICOPStringified>,
    { idField: 'id' }
  ).pipe(
    map(
      (copTestStringified: ICOPStringified): ICOP =>
        fromCOPStringifiedToCOP(copTestStringified)
    ),
    mergeMap((parent: ICOP): Observable<ICOP> => {
      if (!parent.sharded) {
        return of(parent);
      }
      return collectionData(
        query(
          collection(
            user.services.firestore,
            `orgs/${user.orgId}/cop/${testId}/shards`
          ) as CollectionReference<ICOPShardStringified>,
          orderBy('order', 'asc')
        )
      ).pipe(
        map((shards: ICOPShardStringified[]): ICOP => {
          return mergeShards(parent, shards);
        })
      );
    })
  );
};

export const copProvider = (testId: string): Promise<ICOP> => {
  return lastValueFrom<ICOP>(
    authenticatedUserProvider()
      .pipe(switchMap(copTest.bind(null, testId)))
      .pipe(take(1))
  );
};

/**
 * merges the parent document and its shards into a single document for processing
 * @param {Object} parent the parent cop document
 * @param {Array<Object>} shards an array of cop document shards
 * @returns {Object}
 */
const mergeShards = (parent: ICOP, shards: ICOPShardStringified[]): ICOP => {
  return shards.reduce((acc: ICOP, curr: ICOPShardStringified): ICOP => {
    dataKeys.forEach((key: string): void => {
      if (curr.hasOwnProperty(key)) {
        const currentData =
          typeof acc[key] === 'string' ? JSON.parse(acc[key]) : acc[key];
        acc[key] = [].concat(
          currentData,
          JSON.parse(ensureParseableArray(curr[key]))
        );
      }
    });
    return acc;
  }, parent);
};

const fromCOPStringifiedToCOP = (stringified: ICOPStringified): ICOP => {
  const copX: number[] = JSON.parse(ensureParseableArray(stringified.copX));
  const copY: number[] = JSON.parse(ensureParseableArray(stringified.copY));
  const leftCOPX: number[] = JSON.parse(
    ensureParseableArray(stringified.leftCOPX)
  );
  const leftCOPY: number[] = JSON.parse(
    ensureParseableArray(stringified.leftCOPY)
  );

  const rightCOPX: number[] = JSON.parse(
    ensureParseableArray(stringified.rightCOPX)
  );
  const rightCOPY: number[] = JSON.parse(
    ensureParseableArray(stringified.rightCOPY)
  );

  return {
    ...stringified,
    copX,
    copY,
    leftCOPX,
    leftCOPY,
    rightCOPX,
    rightCOPY,
  };
};

const ensureParseableArray = (value: string): string => {
  const parseableArray = '[]';
  if (value === undefined || value === null) return parseableArray;
  if (value === 'NA') return parseableArray;
  return value;
};
